import { useWorkspaceStore } from '../store/workspaceStore';
import { useAuthStore } from '../store/authStore';
import { useCollectionTabSave } from './collections/useCollectionTabSave';
import { useRequestSelectSave } from './collections/useRequestSelectSave';
import { useCollectionDuplicates } from './collections/useCollectionDuplicates';
import { useCollectionCrud } from './collections/useCollectionCrud';
import { useTreeSave } from './collections/useTreeSave';
import { useRequestHistory } from './collections/useRequestHistory';
import { useCollectionModals } from './collections/useCollectionModals';

/**
 * Collection/folder/request domain handlers. Built up across brief-04
 * increments; all domain reads go through getState(). Cross-hook helpers
 * (tab handlers from useTabs, getLiveEditorStateForRequest) and shared imperative
 * refs are passed in by App. Effects (big collections load, tree-save flush,
 * dedupe) stay in App and call these returns.
 *
 * @param {{
 *   toastT: Function,
 *   showToast: Function,
 *   createNewRequestTab: Function,
 *   updateCurrentRequestTabStable: Function,
 *   getCurrentRequestTab: Function,
 *   getLiveEditorStateForRequest: Function,
 *   reloadDeviceLocalCollections: Function,
 *   selectRequestInFlightRef: { current: Map<string, Promise<any>> },
 *   collectionRunnerTargetRef: { current: any },
 *   collectionRunnerAiResolveRef: { current: any },
 * }} deps
 */
export function useCollections({
  toastT, showToast, createNewRequestTab, updateCurrentRequestTabStable, getCurrentRequestTab,
  getLiveEditorStateForRequest, reloadDeviceLocalCollections, selectRequestInFlightRef,
  collectionRunnerTargetRef, collectionRunnerAiResolveRef,
}) {
  // Subscribed reactively only where callback identity must change on workspace
  // switch (tree-save flush) — handlers otherwise read via getState().
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const deviceLocalCollections = useWorkspaceStore((s) => s.deviceLocalCollections);
  const {
    createCollection, openImportCollection, openExportCollection, openRunCollection, openCodeGen,
    handleCollectionRunnerClose, handleCollectionRunnerComplete, getExportableCollections,
    openExportCollectionFromPalette, handleSelectCollectionForExport,
  } = useCollectionModals({ toastT, collectionRunnerTargetRef, collectionRunnerAiResolveRef });

  const {
    selectFolder, selectFromHistory, addToHistory, clearHistory, handleClearHistory,
    clearHistoryForRequest, flashSidebarEntry,
  } = useRequestHistory({
    toastT, createNewRequestTab, updateCurrentRequestTabStable, getCurrentRequestTab,
  });

  const {
    flushTreeSave,
    scheduleTreeSave,
  } = useTreeSave({ toastT, currentWorkspace, isAuthenticated, deviceLocalCollections });

  const {
    handleCreateCollection, createRequest, deleteRequest, deleteCollection,
    persistCollectionSettings, handleCreateFolder, createFolderReturningId, handleDeleteFolder,
    handleSidebarCollectionsReorder,
  } = useCollectionCrud({
    toastT, createNewRequestTab, updateCurrentRequestTabStable, scheduleTreeSave,
  });

  const { duplicateRequest, duplicateCollection, duplicateFolder } = useCollectionDuplicates({
    getLiveEditorStateForRequest, toastT, showToast, flashSidebarEntry, scheduleTreeSave,
    persistCollectionSettings,
  });

  const { selectRequest, saveRequest } = useRequestSelectSave({
    getLiveEditorStateForRequest, toastT, createNewRequestTab, selectRequestInFlightRef,
  });

  const {
    handleSelectCollectionForPendingRequest,
    handleCollectionTabDataChange,
    saveCollectionFromTab,
  } = useCollectionTabSave({ toastT, reloadDeviceLocalCollections, saveRequest });

  return {
    selectRequest, saveRequest, handleSelectCollectionForPendingRequest,
    handleCollectionTabDataChange, saveCollectionFromTab, flashSidebarEntry, scheduleTreeSave,
    flushTreeSave, handleCreateCollection, createRequest, deleteRequest, deleteCollection,
    persistCollectionSettings, handleCreateFolder, createFolderReturningId, handleDeleteFolder,
    handleSidebarCollectionsReorder, duplicateRequest, duplicateCollection, duplicateFolder,
    createCollection, openImportCollection, openExportCollection, openRunCollection, openCodeGen,
    handleCollectionRunnerClose, handleCollectionRunnerComplete, getExportableCollections,
    openExportCollectionFromPalette, handleSelectCollectionForExport, selectFolder,
    selectFromHistory, addToHistory, clearHistory, handleClearHistory, clearHistoryForRequest,
  };
}
